import React, { PureComponent } from 'react';
import { DATE_FORMAT, StatusType } from '../../api/api.types';
import { DeskOpeningExceptionModel, DeskOpeningModel, TimelineOpening } from '../../api/core/core.types';
import { DeskOpeningTimelineObject } from './DeskOpeningTimelineObject';
import { getDeskOpeningTimelineMatrix } from '../../helpers/desk.helper';
import {withTranslation, WithTranslation} from 'react-i18next';
import dayjs, {Dayjs} from 'dayjs';
import {isSameOrBefore} from '../../helpers/date.helper';

interface DeskOpeningTimelineProps extends WithTranslation {
  startDate: string;
  endDate: string;
  deskOpenings: DeskOpeningModel[];
  deskOpeningExceptions: DeskOpeningExceptionModel[];
  selectedDeskOpening?: DeskOpeningModel;
  selectedDeskOpeningException?: DeskOpeningExceptionModel;
  onDeskOpeningClick: (id: number) => void;
  onDeskOpeningExceptionClick: (id: number) => void;
}

interface DeskOpeningTimelineState {
  isScrolling: boolean;
  clientX: number;
  scrollX: number;
}

class DeskOpeningTimeline extends PureComponent<DeskOpeningTimelineProps, DeskOpeningTimelineState> {

  readonly state = {
    isScrolling: false,
    clientX: 0,
    scrollX: 0
  };

  ref: any = React.createRef();

  onMouseDown = (e: any) => {
    this.setState({
      isScrolling: true,
      clientX: e.clientX
    });
  };

  onMouseUp = () => {
    this.setState({ isScrolling: false });
  };

  onMouseMove = (e: any) => {
    const { clientX, scrollX } = this.state;
    if (this.state.isScrolling) {
      this.ref.current.scrollLeft = scrollX - e.clientX + clientX;
      this.setState({
        scrollX: scrollX - e.clientX + clientX,
        clientX: e.clientX
      });
    }
  };

  getTableHeaders = () => {
    const headers: any[] = [];
    let date = dayjs(this.props.startDate);
    const endDate = dayjs(this.props.endDate).subtract(1, 'day');

    let prevMonth = date.month() - 1;
    let monthName;
    while (isSameOrBefore(date, endDate)) {

      if (prevMonth !== date.month()) {
        monthName = `${date.format('MMMM').substring(0, 3)} ${date.format('YYYY')}`;
      } else monthName = undefined;
      headers.push((
        <>
          <th className="timeline-table-column timeline-table-header">
            {monthName && (
              <>
                <div className="timeline-month-name mb-1">{monthName}</div>
              </>
            )}
          </th>
        </>
      ));

      prevMonth = date.month();
      date = date.add(1, 'day');
    }

    return headers;
  };

  getDeskOpeningsTimeline = (timelineOpenings: TimelineOpening[][]) => {
    const timelineRows: any[] = [];
    let inx = 0;
    const { t } = this.props;

    timelineOpenings.forEach((openings, index) => {
      let rowHeader = '';
      if (openings.length) {
        if (index === 0) {
          rowHeader = t('l.desk.holidays');
        } else if (index === 1) {
          rowHeader = t('l.desk.open_hours');
        }
        timelineRows.push(this.getTimelineRow(openings, rowHeader, inx));
        inx++;
      }
    });

    return (
      <>
        <thead>
          <tr>
            <div className="timeline-table-column-first mb-1">
            </div>
            {this.getTableHeaders()}
          </tr>
        </thead>
        <tbody className="timeline-table-body">
          {timelineRows}
        </tbody>
      </>
    );
  };

  pushTimelineObject = (to: TimelineOpening, date: Dayjs, timelineRow: any[], index: number) => {
    let colSpan = 0;
    let validFrom = dayjs(to.validFrom);
    if (dayjs(to.validFrom).isBefore(dayjs(this.props.startDate))) {
      validFrom = dayjs(this.props.startDate);
    }

    while (isSameOrBefore(date, dayjs(to.isException ? to.validFrom : to.validTo))
    && isSameOrBefore(date, dayjs(this.props.endDate).subtract(1, 'day'))) {
      date = this.pushOpening(date, timelineRow, validFrom);

      colSpan += 1;
      date = date.add(1, 'day');
    }

    const objectLabel = !to.isException ? `${dayjs(to.validFrom).format(DATE_FORMAT)} - ${dayjs(to.validTo)
        .format(DATE_FORMAT)}` : '';

    if (colSpan !== 0) {
      timelineRow.push(
        <DeskOpeningTimelineObject
          isFirst={index === 0}
          isException={to.isException}
          deskOpeningId={to.id}
          duration={colSpan}
          objectLabel={objectLabel}
          isInactive={to.statusType === StatusType.INACTIVE ? true : undefined}
          isSelected={this.getSelected(to.isException, to.id)}
          onClick={() => this.timelineObjectClicked(to.id, to.isException)}
        />
      );
    }

    return date;
  };

  pushOpening = (date: Dayjs, timelineRow: any[], validFrom: Dayjs): Dayjs => {
    if (date.isBefore(validFrom)) {
      let whiteSpace = 0;

      while (date.isBefore(validFrom)) {
        whiteSpace += 1;
        date = date.add(1, 'day');
      }

      timelineRow.push((<td key={`whitespace ${date.toString()}`} colSpan={whiteSpace}></td>));
    }
    return date;
  };

  getSelected = (isException: boolean, id: number) => {
    if (isException) {
      return this.props.selectedDeskOpeningException && this.props.selectedDeskOpeningException.id === id ? true : undefined;
    }
    return this.props.selectedDeskOpening && this.props.selectedDeskOpening.id === id ? true : undefined;
  };

  timelineObjectClicked = (id: number, isException: boolean) => {
    if (isException) {
      this.props.onDeskOpeningExceptionClick(id);
    } else {
      this.props.onDeskOpeningClick(id);
    }
  };

  getTimelineRow = (timelineOpenings: TimelineOpening[], rowHeader: string, index: number) => {
    let date = dayjs(this.props.startDate);
    const timelineRow: any[] = [];

    timelineOpenings.forEach((d) => {
      date = this.pushTimelineObject({
        validFrom: d.validFrom,
        id: d.id,
        isException: d.isException,
        statusType: d.statusType,
        validTo: d.validTo
      }, date, timelineRow, index);
    });

    timelineRow.push((<td colSpan={1000}></td>));

    return (
      <tr>
        <td className={this.getRowClass(index, rowHeader)}>
          <div className="timeline-row-header pl-2 pr-2">{rowHeader}</div>
        </td>
        {timelineRow}
      </tr>
    );
  };

  getRowClass = (index: number, header: string) => {
    const className = header === 'Opening hours' ? 'standard-rate-cell-header' : 'adjustment-cell';
    return className.concat(index === 0 ? ' pt-1' : '');
  };

  getTimeline = () => (
    <table className="timeline-table">
      {this.getDeskOpeningsTimeline(getDeskOpeningTimelineMatrix(this.props.deskOpenings, this.props.deskOpeningExceptions))}
    </table>
  );

  render() {
    return (
      <div
        className="container-fluid scrolling-timeline padding-bottom-20"
        ref={this.ref}
        onMouseDown={(e: any) => this.onMouseDown(e)}
        onMouseUp={this.onMouseUp}
        onMouseMove={(e: any) => this.onMouseMove(e)}
        onMouseLeave={() => this.setState({ isScrolling: false })}
      >
        {this.getTimeline()}
      </div>
    );
  }
}

export default withTranslation()(DeskOpeningTimeline);
